import { templateService } from '../../services';
import { registerSessionScoped } from '../../store/session-scope';
import { dayjs } from '../date';

import type { ImportableWorkoutSource } from '../../services';
import type {
  LibraryWorkoutListResponse,
  PastWorkoutListResponse,
} from '../../types/api/template';

/**
 * Importable Workouts
 *
 * The lists a plan's "Import Workout" step picks from — the account's library
 * and its past workouts — read through one cache, so the web modal and the
 * mobile card ask the server once between them and not on every open.
 *
 * - Library workouts stay fresh for an hour (they rarely change).
 * - Past workouts stay fresh for five minutes (finishing a workout adds one).
 * - Two callers asking at once share one request.
 *
 * Each platform wraps this in its own thin hook for loading and error state.
 */

/** What a source's list comes back as. */
export type ImportableWorkoutsData<T extends ImportableWorkoutSource> =
  T extends 'library' ? LibraryWorkoutListResponse : PastWorkoutListResponse;

type AnyImportableWorkouts =
  LibraryWorkoutListResponse | PastWorkoutListResponse;

const STALE_TIME: Record<ImportableWorkoutSource, number> = {
  library: 60 * 60 * 1000,
  past: 5 * 60 * 1000,
};

const cache = new Map<
  ImportableWorkoutSource,
  { data: AnyImportableWorkouts; timestamp: number }
>();

const activeRequests = new Map<
  ImportableWorkoutSource,
  Promise<AnyImportableWorkouts>
>();

/** The cached list for a source, if there is one and it is still fresh. */
export function readImportableWorkouts<T extends ImportableWorkoutSource>(
  source: T
): ImportableWorkoutsData<T> | null {
  const cached = cache.get(source);
  if (!cached) return null;

  const fresh = dayjs().valueOf() - cached.timestamp < STALE_TIME[source];
  return fresh ? (cached.data as ImportableWorkoutsData<T>) : null;
}

/**
 * Fetches a source's list and caches it. A caller that arrives while another's
 * request is in flight shares its answer; if that request fails, the caller
 * makes its own.
 */
export async function fetchImportableWorkouts<
  T extends ImportableWorkoutSource,
>(source: T): Promise<ImportableWorkoutsData<T>> {
  const active = activeRequests.get(source);
  if (active) {
    try {
      return (await active) as ImportableWorkoutsData<T>;
    } catch {
      // The shared request failed; ask again.
    }
  }

  const request = templateService.getImportableWorkouts(source);
  activeRequests.set(source, request);

  try {
    const data = await request;
    cache.set(source, { data, timestamp: dayjs().valueOf() });
    return data;
  } finally {
    // Only this caller's request: a later one may have taken its place.
    if (activeRequests.get(source) === request) activeRequests.delete(source);
  }
}

/** Forgets the cached lists — one source's, or both. */
export function clearImportableWorkoutsCache(
  source?: ImportableWorkoutSource
): void {
  if (source) {
    cache.delete(source);
  } else {
    cache.clear();
  }
}

/** Forgets the past workouts — call after a workout is completed. */
export function invalidatePastWorkoutsCache(): void {
  clearImportableWorkoutsCache('past');
}

// The lists are the account's own workouts and the ones it completed lately.
registerSessionScoped('importableWorkouts', () => {
  clearImportableWorkoutsCache();
});
